import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye,
  Phone,
  Mail,
  MapPin,
  User,
  Stethoscope,
  Shield,
  UserCheck
} from 'lucide-react';
import { UserProfile } from '../types';
import ProfessionalForm from '../components/ProfessionalForm';
import ProfessionalDetails from '../components/ProfessionalDetails';
import { useProfessionals } from '../hooks/useProfessionals';

export default function Professionals() {
  const { userProfile } = useAuth();
  const { professionals, loading, deleteProfessional } = useProfessionals();
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState<UserProfile | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const filteredProfessionals = professionals.filter(professional =>
    professional.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    professional.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    professional.cpf.includes(searchTerm) ||
    professional.registrationNumber.includes(searchTerm)
  );

  const handleEdit = (professional: UserProfile) => {
    setSelectedProfessional(professional);
    setShowForm(true);
  };

  const handleDelete = async (professional: UserProfile) => {
    if (window.confirm(`Tem certeza que deseja excluir o profissional ${professional.name}?`)) {
      await deleteProfessional(professional.id);
    }
  };

  const handleViewDetails = (professional: UserProfile) => {
    setSelectedProfessional(professional);
    setShowDetails(true);
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'medico':
        return Stethoscope;
      case 'enfermeiro':
        return UserCheck;
      case 'admin':
      case 'coordenador':
        return Shield;
      default:
        return User;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'medico':
        return 'bg-blue-100 text-blue-800';
      case 'enfermeiro':
        return 'bg-green-100 text-green-800';
      case 'psicologo':
        return 'bg-purple-100 text-purple-800';
      case 'assistente_social':
        return 'bg-orange-100 text-orange-800';
      case 'coordenador':
        return 'bg-red-100 text-red-800';
      case 'admin':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const getRoleName = (role: string) => {
    const roles = {
      'admin': 'Administrador',
      'coordenador': 'Coordenador',
      'medico': 'Médico',
      'enfermeiro': 'Enfermeiro',
      'psicologo': 'Psicólogo',
      'assistente_social': 'Assistente Social',
      'recepcionista': 'Recepcionista'
    };
    return roles[role as keyof typeof roles] || role;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-medical-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profissionais</h1>
          <p className="text-gray-600">Gerencie os profissionais cadastrados no sistema</p>
        </div>
        {(userProfile?.role === 'admin' || userProfile?.role === 'coordenador') && (
          <button
            onClick={() => {
              setSelectedProfessional(null);
              setShowForm(true);
            }}
            className="bg-medical-600 text-white px-4 py-2 rounded-lg hover:bg-medical-700 flex items-center space-x-2 transition-colors"
          >
            <Plus className="h-5 w-5" />
            <span>Novo Profissional</span>
          </button>
        )}
      </div>

      {/* Search */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
          <input
            type="text"
            placeholder="Buscar por nome, email, CPF ou registro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-medical-500 focus:border-medical-500"
          />
        </div>
      </div>

      {/* Professionals Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProfessionals.map((professional) => {
          const RoleIcon = getRoleIcon(professional.role);
          return (
            <div key={professional.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-medical-100 rounded-full flex items-center justify-center">
                      <RoleIcon className="h-6 w-6 text-medical-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{professional.name}</h3>
                      <p className="text-sm text-gray-500">{professional.position}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    professional.status === 'ativo' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {professional.status}
                  </span>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(professional.role)}`}>
                      {getRoleName(professional.role)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Phone className="h-4 w-4" />
                    <span>{professional.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span>{professional.email}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPin className="h-4 w-4" />
                    <span>{professional.unit}</span>
                  </div>
                  {professional.registrationNumber && (
                    <div className="text-xs text-gray-500">
                      Registro: {professional.registrationNumber}
                    </div>
                  )}
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      CPF: {professional.cpf}
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewDetails(professional)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      {(userProfile?.role === 'admin' || userProfile?.role === 'coordenador') && (
                        <>
                          <button
                            onClick={() => handleEdit(professional)}
                            className="p-2 text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(professional)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Excluir"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {filteredProfessionals.length === 0 && (
        <div className="text-center py-12">
          <User className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum profissional encontrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            {searchTerm ? 'Tente ajustar os termos de busca.' : 'Comece cadastrando um novo profissional.'}
          </p>
        </div>
      )}

      {/* Professional Form Modal */}
      {showForm && (
        <ProfessionalForm
          professional={selectedProfessional}
          onClose={() => {
            setShowForm(false);
            setSelectedProfessional(null);
          }}
        />
      )}

      {/* Professional Details Modal */}
      {showDetails && selectedProfessional && (
        <ProfessionalDetails
          professional={selectedProfessional}
          onClose={() => {
            setShowDetails(false);
            setSelectedProfessional(null);
          }}
        />
      )}
    </div>
  );
}